import { addDays, differenceInCalendarDays, parseISO, startOfDay } from "date-fns";
import { DeliveryDateSettings } from "../types/DeliveryDateSettings";

export type DateInput = Date | string | null | undefined;

// The API serializes DateOnly values as plain "yyyy-MM-dd" strings, which api-client.ts does not convert.
// parseISO reads them as local midnight; new Date("yyyy-MM-dd") would read them as UTC midnight (previous evening in US zones).
export function toDate(value: DateInput): Date | undefined {
	if (value === null || value === undefined || value === '') return undefined;
	const parsed = value instanceof Date ? value : parseISO(value);
	return isNaN(parsed.getTime()) ? undefined : parsed;
}

export function isWeekday(date: Date): boolean {
	const day = date.getDay();
	return day !== 0 && day !== 6;
}

// Mirrors SageSync.Api.Services.CfiRouting.CfiRoutingService.IsDeliveryDateValid: keep the two in sync.
export function validateDeliveryDate(date: Date | undefined, settings: DeliveryDateSettings): boolean {
	if (!date) return false;

	const day = startOfDay(date);
	const startDate = startOfDay(toDate(settings.rangeOfRecurrenceStartDate) ?? new Date());
	const endDate = toDate(settings.rangeOfRecurrenceEndDate);

	const cutoffDate = getCutoffDate(applyCutoffTime(day, settings.cutoffTime), settings.cutoffDaysBefore ?? 1);
	if (new Date() > cutoffDate) return false;

	if (day < startDate) return false;

	if (settings.rangeOfRecurrenceStatus === 2 && endDate && day > startOfDay(endDate)) return false; // EndByDate

	if (settings.rangeOfRecurrenceStatus === 1 && settings.occurrence && settings.occurrence > 0) { // Occurrence
		let occurrenceCount = 0;
		for (let current = startDate; current < day; current = addDays(current, 1)) {
			if (isDateValidByPattern(current, settings, startDate)) {
				occurrenceCount++;
				if (occurrenceCount >= settings.occurrence) return false;
			}
		}
	}

	return isDateValidByPattern(day, settings, startDate);
}

// Mirrors CfiRoutingService.GetCutoffDate: step back one calendar day at a time and count only weekdays.
// The delivery day itself is never counted and public holidays are ordinary days.
export function getCutoffDate(date: Date, cutoffDaysBefore: number | undefined): Date {
	let cutoffDate = new Date(date);
	let daysSubtracted = 0;
	while (daysSubtracted < (cutoffDaysBefore ?? 0)) {
		cutoffDate = addDays(cutoffDate, -1);
		if (isWeekday(cutoffDate)) daysSubtracted++;
	}
	return cutoffDate;
}

export function getClosestDeliveryDate(settings: DeliveryDateSettings): Date | undefined {
	const date = new Date();
	for (let i = 0; i <= 365; i++) {
		if (validateDeliveryDate(date, settings)) {
			return date;
		}

		date.setDate(date.getDate() + 1);
	}

	return;
}

function applyCutoffTime(day: Date, cutoffTime: string | null | undefined): Date {
	const [hours = 0, minutes = 0, seconds = 0] = (cutoffTime ?? '00:00:00').split(':').map(Number);
	return new Date(day.getFullYear(), day.getMonth(), day.getDate(), hours, minutes, seconds);
}

function isDateValidByPattern(day: Date, settings: DeliveryDateSettings, startDate: Date): boolean {
	switch (settings.recurrencePatternType) {
		case 1: // Daily (weekdays only)
			return isWeekday(day);
		case 2: { // Weekly
			if (!settings.recurrenceWeeklyWeekDays || !checkDay(day, buildWeekDaysArray(settings.recurrenceWeeklyWeekDays))) return false;
			const periodicity = settings.recurrenceWeeklyPeriodicity ?? 1;
			if (periodicity > 1) {
				const weekStartDate = addDays(startDate, -startDate.getDay()); // first week starts on the Sunday of the start date
				const weeksDifference = Math.floor(differenceInCalendarDays(weekStartDate, day) / 7);
				if (weeksDifference % periodicity !== 0) return false;
			}
			return true;
		}
		case 3: { // Monthly
			if (day.getDate() !== settings.recurrenceMonthlyDay) return false;
			const periodicity = settings.recurrenceMonthlyDayOfEveryMonth ?? 1;
			if (periodicity > 1) {
				const monthDifference = (day.getFullYear() - startDate.getFullYear()) * 12 + day.getMonth() - startDate.getMonth();
				if (monthDifference % periodicity !== 0) return false;
			}
			return true;
		}
		case 4: { // Yearly
			if (day.getMonth() + 1 !== settings.recurrenceYearlyOnMonth || day.getDate() !== settings.recurrenceYearlyOnMonthDay) return false;
			const periodicity = settings.recurrenceYearlyPeriodicity ?? 1;
			if (periodicity > 1 && (day.getFullYear() - startDate.getFullYear()) % periodicity !== 0) return false;
			return true;
		}
		default: // None or unknown: weekdays, like the backend
			return isWeekday(day);
	}
}

// Bitmask: Monday=1, Tuesday=2, Wednesday=4, Thursday=8, Friday=16, Saturday=32 (JS getDay(): Monday=1 ... Saturday=6)
function buildWeekDaysArray(weekdays: number): number[] {
	const validDays: number[] = [];
	for (let i = 0; i < 6; i++) {
		if ((weekdays & (1 << i)) > 0) {
			validDays.push(i + 1);
		}
	}
	return validDays;
}

function checkDay(date: Date, validDays: number[]): boolean {
	return validDays.includes(date.getDay());
}
